import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Section } from "@/components/PageHeader";
import { Trophy, Medal, Zap, Rocket, Dumbbell } from "lucide-react";

const AVATAR_COLORS = [
  "bg-pink-500", "bg-violet-500", "bg-emerald-500", "bg-orange-500",
  "bg-rose-500", "bg-sky-500", "bg-amber-500", "bg-indigo-500",
];

const METRICS = [
  { key: "efficiency", label: "Efficiency", suffix: "%", description: "Benchmark ÷ actual time, this month" },
  { key: "hours", label: "Hours logged", suffix: "h", description: "Total tracked time, this month" },
  { key: "tasks", label: "Tasks done", suffix: "", description: "Completed tasks with logged time, this month" },
];

function minutesToHuman(m) {
  if (!m || m <= 0) return "0h 0m";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm}m`;
}

function Avatar({ name, idx, size = 40 }) {
  const initials = (name || "?")
    .split(" ")
    .map(p => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      className={`${AVATAR_COLORS[idx % AVATAR_COLORS.length]} text-white rounded-full grid place-items-center font-semibold shrink-0`}
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials}
    </div>
  );
}

function BadgeRow({ row }) {
  const badges = [];
  if (row.efficiency_pct >= 150) badges.push({ Icon: Zap, color: "text-amber-500", title: "Speed — 150%+ efficiency" });
  if (row.tasks_done >= 5) badges.push({ Icon: Dumbbell, color: "text-violet-600", title: "Powerhouse — 5+ tasks done" });
  if (row.hours_minutes >= 600) badges.push({ Icon: Rocket, color: "text-rose-500", title: "Rocket — 10h+ logged" });
  if (badges.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5">
      {badges.map(({ Icon, color, title }) => (
        <Icon key={title} size={14} className={color} aria-label={title} />
      ))}
    </div>
  );
}

function PodiumCard({ row, idx }) {
  const heights = ["h-44", "h-36", "h-32"];
  const orderClasses = ["order-2", "order-1 lg:order-1", "order-3"];
  const accents = ["from-amber-400 to-amber-600", "from-neutral-300 to-neutral-500", "from-orange-400 to-orange-700"];
  const RankIcon = idx === 0 ? Trophy : Medal;
  const iconColors = ["text-amber-500", "text-neutral-400", "text-orange-600"];

  return (
    <div className={`flex flex-col items-center ${orderClasses[idx]} flex-1 min-w-0`}>
      <div className="flex flex-col items-center mb-3">
        <RankIcon size={idx === 0 ? 28 : 22} className={iconColors[idx]} />
        <Avatar name={row.name} idx={idx} size={idx === 0 ? 64 : 52} />
        <div className="mt-2 text-center min-w-0 max-w-[160px]">
          <div className="text-sm font-semibold truncate">{row.name}</div>
          <div className="text-[11px] text-neutral-500 truncate">{row.email}</div>
        </div>
      </div>
      <div className={`w-full ${heights[idx]} bg-gradient-to-b ${accents[idx]} text-white px-4 py-3 flex flex-col items-center justify-end relative`}>
        <div className="absolute top-2 left-1/2 -translate-x-1/2 text-[40px] font-display font-bold opacity-20 leading-none">
          {idx + 1}
        </div>
        <div className="font-display text-2xl font-bold tabular-nums">{row.score_display}</div>
        <div className="text-[10px] uppercase tracking-widest opacity-80">{row.metric_label}</div>
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const [metric, setMetric] = useState("efficiency");
  const [cache, setCache] = useState({ efficiency: null, hours: null, tasks: null });
  const [loading, setLoading] = useState(true);

  // Fetch all three metrics once on mount; switch client-side after that.
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/leaderboard?metric=efficiency&limit=20").then(r => r.data).catch(() => []),
      api.get("/leaderboard?metric=hours&limit=20").then(r => r.data).catch(() => []),
      api.get("/leaderboard?metric=tasks&limit=20").then(r => r.data).catch(() => []),
    ])
      .then(([efficiency, hours, tasks]) => setCache({ efficiency, hours, tasks }))
      .finally(() => setLoading(false));
  }, []);

  const rows = cache[metric] || [];
  const meta = METRICS.find(m => m.key === metric);

  const enriched = rows.map(r => {
    let display;
    if (metric === "hours") display = minutesToHuman(r.hours_minutes);
    else if (metric === "tasks") display = `${r.tasks_done}`;
    else display = `${r.efficiency_pct}%`;
    return { ...r, score_display: display, metric_label: meta.label };
  });

  const top3 = enriched.slice(0, 3);
  const rest = enriched.slice(3);

  return (
    <div data-testid="leaderboard-page">
      <PageHeader
        eyebrow="Recognition"
        title="Leaderboard"
        description="Friendly competition. Updated live from logged hours, completed tasks and benchmark adherence."
        testid="leaderboard-header"
      />

      <Section>
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="leaderboard-metric-tabs">
          {METRICS.map(m => (
            <button
              key={m.key}
              onClick={() => setMetric(m.key)}
              data-testid={`leaderboard-tab-${m.key}`}
              className={`px-4 py-2 text-sm border rounded-sm transition-colors ${
                metric === m.key
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "border-neutral-200 text-neutral-600 hover:bg-neutral-50"
              }`}
            >
              {m.label}
            </button>
          ))}
          <span className="text-xs text-neutral-500 ml-2">{meta.description}</span>
        </div>

        {loading ? (
          <div className="border border-neutral-200 bg-white p-12 text-center text-sm text-neutral-500">Loading…</div>
        ) : enriched.length === 0 ? (
          <div className="border border-neutral-200 bg-white p-12 text-center text-sm text-neutral-500" data-testid="leaderboard-empty">
            No activity for this metric yet. Once the team logs time, ranks will appear here.
          </div>
        ) : (
          <>
            {/* Podium */}
            {top3.length > 0 && (
              <div className="relative border border-neutral-200 bg-white p-8 mb-6 overflow-hidden" data-testid="leaderboard-podium">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500" />
                <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-semibold mb-1">Top 3</div>
                <div className="font-display text-xl font-bold mb-8">Podium · {meta.label}</div>
                <div className="flex items-end gap-4 lg:gap-6 max-w-3xl mx-auto">
                  {/* Re-order so 1st is center, 2nd left, 3rd right via order classes */}
                  {top3.map((r, i) => <PodiumCard key={r.user_id} row={r} idx={i} />)}
                </div>
              </div>
            )}

            {/* Full ranking */}
            <div className="relative border border-neutral-200 bg-white overflow-hidden" data-testid="leaderboard-table-card">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" />
              <div className="px-6 py-4 border-b border-neutral-200 bg-gradient-to-r from-indigo-50 to-white">
                <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-700 font-semibold mb-1">Full ranking</div>
                <div className="font-display text-lg font-bold">Everyone with activity this month</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="leaderboard-table">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-100">
                      <th className="text-left font-semibold px-6 py-3 w-16">Rank</th>
                      <th className="text-left font-semibold px-3 py-3">Member</th>
                      <th className="text-right font-semibold px-3 py-3">Hours</th>
                      <th className="text-right font-semibold px-3 py-3">Tasks</th>
                      <th className="text-right font-semibold px-3 py-3">Efficiency</th>
                      <th className="text-right font-semibold px-6 py-3">{meta.label}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enriched.map((r, i) => (
                      <tr key={r.user_id} className="border-b border-neutral-50 hover:bg-neutral-50 transition-colors" data-testid={`leaderboard-row-${i}`}>
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-2">
                            {i < 3 ? (
                              i === 0 ? <Trophy size={16} className="text-amber-500" />
                                     : <Medal size={16} className={i === 1 ? "text-neutral-400" : "text-orange-600"} />
                            ) : null}
                            <span className="text-sm font-semibold text-neutral-700 tabular-nums">#{r.rank}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-3">
                            <Avatar name={r.name} idx={i} size={36} />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium truncate">{r.name}</span>
                                <BadgeRow row={r} />
                              </div>
                              <div className="text-[11px] text-neutral-500 truncate">{r.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums">{minutesToHuman(r.hours_minutes)}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{r.tasks_done}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{r.efficiency_pct}%</td>
                        <td className="px-6 py-3 text-right font-display font-bold tabular-nums">{r.score_display}</td>
                      </tr>
                    ))}
                    {rest.length === 0 && top3.length > 0 && (
                      <tr><td colSpan={6} className="p-6 text-center text-xs text-neutral-400">Only {top3.length} member{top3.length === 1 ? "" : "s"} have activity this month.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </Section>
    </div>
  );
}
